import axios, {
  AxiosError,
  AxiosInstance,
  AxiosProgressEvent,
  AxiosResponse,
  CreateAxiosDefaults,
} from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { acquireTokenSilent } from '../auth'
import Config from '../config'
import { Constants } from '../constants'
import { getCurrentTenantConfig } from '../tenants'
import { enrichErrorWithI18nMessage } from './enrichErrorWithI18nMessage'
import { isMaintenanceError } from './isMaintenanceError'

export class AxiosInstanceBuilder {
  private axiosConfig: CreateAxiosDefaults
  private useAuthentication = false
  private useCorrelationId = false
  private useTenantHeader = false
  private useProjectHeader = false
  private maintenanceHandler: ((value: boolean) => void) | null = null

  constructor() {
    this.axiosConfig = {}
  }

  public withMaintenanceHandler(
    handler: (value: boolean) => void,
  ): AxiosInstanceBuilder {
    this.maintenanceHandler = handler
    return this
  }

  public withBaseUrl(): AxiosInstanceBuilder {
    this.axiosConfig.baseURL = Config.api.baseUrl

    return this
  }

  public withTenantHeader(): AxiosInstanceBuilder {
    this.useTenantHeader = true
    return this
  }

  public withProjectHeader(): AxiosInstanceBuilder {
    this.useProjectHeader = true
    return this
  }

  public withAuthentication(): AxiosInstanceBuilder {
    this.useAuthentication = true
    return this
  }

  public withCorrelationId(): AxiosInstanceBuilder {
    this.useCorrelationId = true
    return this
  }

  public withOnDownloadProgress(
    onDownloadProgress: (progressEvent: AxiosProgressEvent) => void,
  ): AxiosInstanceBuilder {
    this.axiosConfig.onDownloadProgress = onDownloadProgress
    return this
  }

  public withOnUploadProgress(
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
  ): AxiosInstanceBuilder {
    this.axiosConfig.onUploadProgress = onUploadProgress
    return this
  }

  public build(): AxiosInstance {
    const instance = axios.create(this.axiosConfig)

    if (this.useCorrelationId) {
      this.addCorrelationIdRequestInterceptor(instance)
    }
    if (this.useAuthentication) {
      this.addAuthenticationRequestInterceptor(instance)
    }
    if (this.useTenantHeader) {
      this.addTenantHeaderRequestInterceptor(instance)
    }
    if (this.useProjectHeader) {
      this.addProjectHeaderRequestInterceptor(instance)
    }
    if (this.maintenanceHandler) {
      this.addMaintenanceInterceptor(instance)
    }

    this.addI18nMessageResponseInterceptor(instance)

    return instance
  }

  private addMaintenanceInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(
      response => response,
      async error => {
        const isInMaintenanceMode = isMaintenanceError(error)

        if (isInMaintenanceMode && this.maintenanceHandler) {
          this.maintenanceHandler(true)

          return Promise.resolve({
            data: null,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: error.config,
          })
        }

        return Promise.reject(error)
      },
    )
  }

  private addCorrelationIdRequestInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(config => {
      config.headers.set(Constants.HttpHeaderCorrelationId, uuidv4())
      return config
    })
  }

  private addAuthenticationRequestInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(async config => {
      const token = await acquireTokenSilent()
      config.headers.setAuthorization(`Bearer ${token}`)
      return config
    })
  }

  private addTenantHeaderRequestInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(config => {
      const currentTenantConfig = getCurrentTenantConfig()
      if (currentTenantConfig?.tenantId) {
        config.headers.set(
          Constants.HttpHeaderTenantId,
          currentTenantConfig?.tenantId as string,
        )
      }

      return config
    })
  }

  private addProjectHeaderRequestInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(config => {
      const currentTenantConfig = getCurrentTenantConfig()
      if (currentTenantConfig?.projectId) {
        config.headers.set(
          Constants.HttpHeaderProjectId,
          currentTenantConfig?.projectId as string,
        )
      }

      return config
    })
  }

  private addI18nMessageResponseInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const updatedError = enrichErrorWithI18nMessage(error)

        return Promise.reject(updatedError)
      },
    )
  }
}
